import { useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  FileUp,
  FileDown,
  Sparkles,
  RotateCcw,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { exportPDF } from "@/lib/pdfExport";
import { downloadCSVSample, importCSV } from "@/lib/csvIO";
import { track } from "@/lib/analytics";
import { useT } from "@/lib/i18n";

export default function ActionsBar({
  onLoadSample,
  onReset,
  onImport,
  trips,
  result,
  company,
  user,
}) {
  const { t } = useT();
  const fileRef = useRef(null);

  const handleImportClick = () => fileRef.current?.click();
  const handleImportChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const { trips: parsed, skipped } = await importCSV(f);
      onImport(parsed);
      const skipMsg = skipped ? t("actions.csvSkipped", { n: skipped }) : "";
      toast.success(t("actions.csvOk", { n: parsed.length }) + skipMsg);
    } catch (err) {
      toast.error(t("actions.csvErr", { msg: err.message }));
    }
    e.target.value = "";
  };

  return (
    <div
      className="rounded-xl border border-[#272A35] bg-[#131620] p-4 md:p-5 flex flex-wrap gap-2"
      data-testid="actions-bar"
    >
      <Button
        variant="secondary"
        className="bg-[#1A1E2C] border border-[#363A49] hover:bg-[#272A35] text-slate-100"
        onClick={() => {
          track("load_sample");
          onLoadSample();
        }}
        data-testid="load-sample-btn"
      >
        <Sparkles className="h-4 w-4 mr-1.5 text-[#D4AF37]" />
        {t("actions.loadSample")}
      </Button>

      <Button
        variant="secondary"
        className="bg-[#1A1E2C] border border-[#363A49] hover:bg-[#272A35] text-slate-100"
        onClick={async () => {
          try {
            await exportPDF(result, trips, { company, user });
            track("export_pdf", { trips: (trips || []).length });
            toast.success(t("actions.pdfOk"));
          } catch {
            toast.error(t("actions.pdfErr"));
          }
        }}
        data-testid="export-pdf-btn"
      >
        <FileText className="h-4 w-4 mr-1.5" />
        {t("actions.exportPdf")}
      </Button>

      <Button
        variant="secondary"
        className="bg-[#1A1E2C] border border-[#363A49] hover:bg-[#272A35] text-slate-100"
        onClick={handleImportClick}
        data-testid="import-csv-btn"
      >
        <FileUp className="h-4 w-4 mr-1.5" />
        {t("actions.importCsv")}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={handleImportChange}
        data-testid="import-csv-input"
      />

      <Button
        variant="ghost"
        className="text-slate-300 hover:bg-[#1A1E2C] hover:text-slate-100"
        onClick={() => {
          downloadCSVSample();
          track("download_csv_sample");
          toast.success(t("actions.csvSample"));
        }}
        data-testid="download-csv-sample-btn"
      >
        <FileDown className="h-4 w-4 mr-1.5 text-[#D4AF37]" />
        {t("actions.downloadCsvSample")}
      </Button>

      <div className="ml-auto">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              className="text-red-400 hover:bg-red-500/10 hover:text-red-300 border border-red-500/20"
              data-testid="reset-btn"
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              {t("actions.reset")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#131620] border-[#272A35]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">
                {t("actions.resetConfirmTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                {t("actions.resetConfirmSub")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                className="bg-[#1A1E2C] border-[#363A49] text-slate-100 hover:bg-[#272A35] hover:text-slate-50"
                data-testid="reset-cancel"
              >
                {t("actions.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  onReset();
                  track("reset_all");
                  toast.success(t("actions.resetDone"));
                }}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="reset-confirm"
              >
                {t("actions.confirmDelete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
